import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  Calendar,
  Users,
  Send,
  ThumbsUp,
  ThumbsDown,
  Star,
  Video,
  Type,
  Megaphone
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

const NetworkFeed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState('');
  const [reactions, setReactions] = useState({});

  useEffect(() => {
    loadNetworkFeed();
  }, []);

  const loadNetworkFeed = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/network/feed`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
        
        // Load reactions for each post
        const reactionsData = {};
        for (const post of data.posts || []) {
          reactionsData[post.id] = {
            likes: post.likes || 0,
            dislikes: post.dislikes || 0,
            user_reaction: post.user_reaction || null
          };
        }
        setReactions(reactionsData);
      }
    } catch (error) {
      console.error('Error loading network feed:', error);
      toast.error('Failed to load network feed');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async (postId) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/network/posts/${postId}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(prev => ({
          ...prev,
          [postId]: data.comments || []
        }));
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleReaction = async (postId, reactionType) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/network/posts/${postId}/react`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reaction_type: reactionType })
      });

      if (response.ok) {
        const data = await response.json();
        setReactions(prev => ({
          ...prev,
          [postId]: {
            likes: data.likes,
            dislikes: data.dislikes,
            user_reaction: data.user_reaction
          }
        }));
      }
    } catch (error) {
      console.error('Error reacting to post:', error);
      toast.error('Failed to react to post');
    }
  };

  const handleAddComment = async (postId) => {
    if (!newComment.trim()) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/network/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: newComment })
      });

      if (response.ok) {
        setNewComment('');
        loadComments(postId);
        toast.success('Comment added successfully');
      } else {
        toast.error('Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getContentTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return <Video className="h-4 w-4" />;
      case 'announcement':
        return <Megaphone className="h-4 w-4" />;
      default:
        return <Type className="h-4 w-4" />;
    }
  };

  const getContentTypeColor = (type) => {
    switch (type) {
      case 'video':
        return 'bg-blue-100 text-blue-800';
      case 'announcement':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && posts.length === 0) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading network feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Network Feed</h1>
        <p className="text-gray-600 mt-1">
          Stay updated with the latest announcements and community content
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-lg font-bold text-gray-900">{posts.length}</p>
                <p className="text-gray-600 text-sm">New Posts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-lg font-bold text-gray-900">145</p>
                <p className="text-gray-600 text-sm">Active Members</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Heart className="h-5 w-5 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-lg font-bold text-gray-900">2.4k</p>
                <p className="text-gray-600 text-sm">Total Likes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Posts Feed */}
      <div className="space-y-6">
        {posts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No posts available</h3>
              <p className="text-gray-600">
                Check back later for new announcements and updates from the team
              </p>
            </CardContent>
          </Card>
        ) : (
          posts.map((post) => (
            <Card key={post.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start space-x-4">
                  <Avatar>
                    <AvatarImage src={post.author?.avatar} />
                    <AvatarFallback className="bg-yellow-100 text-yellow-800">
                      {post.author?.full_name?.charAt(0) || 'A'}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {post.title}
                      </h3>
                      {post.is_featured && (
                        <Badge className="bg-yellow-100 text-yellow-800">
                          <Star className="h-3 w-3 mr-1" />
                          Featured
                        </Badge>
                      )}
                      <Badge variant="outline" className={`${getContentTypeColor(post.content_type)}`}>
                        {getContentTypeIcon(post.content_type)}
                        <span className="ml-1 capitalize">{post.content_type}</span>
                      </Badge>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-500 space-x-4">
                      <span className="font-medium">
                        {post.author?.full_name || 'BBC Admin'}
                      </span>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(post.created_at)}
                      </div>
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        {post.views || 0} views
                      </div>
                    </div>
                    
                    {post.description && (
                      <p className="text-gray-700 mt-2">{post.description}</p>
                    )}
                  </div>
                </div>
              </CardHeader>

              {/* Post Content */}
              {(post.html_content || post.video_embed) && (
                <CardContent>
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <div dangerouslySetInnerHTML={{ 
                      __html: post.html_content || post.video_embed 
                    }} />
                  </div>
                </CardContent>
              )}

              {/* Interaction Bar */}
              <CardContent className="pt-0">
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center space-x-4">
                    {/* Like/Dislike */}
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReaction(post.id, 'like')}
                        className={`${
                          reactions[post.id]?.user_reaction === 'like'
                            ? 'text-blue-600 bg-blue-50'
                            : 'text-gray-600'
                        }`}
                      >
                        <ThumbsUp className="h-4 w-4 mr-1" />
                        {reactions[post.id]?.likes || 0}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReaction(post.id, 'dislike')}
                        className={`${
                          reactions[post.id]?.user_reaction === 'dislike'
                            ? 'text-red-600 bg-red-50'
                            : 'text-gray-600'
                        }`}
                      >
                        <ThumbsDown className="h-4 w-4 mr-1" />
                        {reactions[post.id]?.dislikes || 0}
                      </Button>
                    </div>

                    {/* Comments */}
                    {post.allow_comments && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedPost(selectedPost === post.id ? null : post.id);
                          if (selectedPost !== post.id) {
                            loadComments(post.id);
                          }
                        }}
                        className="text-gray-600"
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        {post.comments_count || 0} Comments
                      </Button>
                    )}
                  </div>

                  {/* Share */}
                  <Button variant="ghost" size="sm" className="text-gray-600">
                    <Share2 className="h-4 w-4 mr-1" />
                    Share
                  </Button>
                </div>

                {/* Comments Section */}
                {selectedPost === post.id && post.allow_comments && (
                  <div className="mt-4 pt-4 border-t">
                    {/* Add Comment */}
                    <div className="flex space-x-3 mb-4">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-blue-100 text-blue-800 text-xs">
                          {user?.full_name?.charAt(0) || 'M'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex space-x-2">
                          <Input
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Write a comment..."
                            className="flex-1"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAddComment(post.id);
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleAddComment(post.id)}
                            disabled={!newComment.trim()}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Comments List */}
                    <div className="space-y-3">
                      {(comments[post.id] || []).map((comment) => (
                        <div key={comment.id} className="flex space-x-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-gray-100 text-gray-600 text-xs">
                              {comment.author?.full_name?.charAt(0) || 'M'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="font-medium text-sm">
                                {comment.author?.full_name || 'Member'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(comment.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{comment.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default NetworkFeed;